// Typed calls for the teacher's check-in workflow (Workflow I,
// supabase/migrations/0017_teacher_sessions.sql). Every function checks the
// caller's active role and class assignment itself; nothing here reads a
// table directly, and results only ever come back already suppressed.
import { rpc as baseRpc, type Result } from '../admin/api';
export { errorText } from '../admin/api';
export type { AppError, Result } from '../admin/api';

// The access checks raise plain "not authorized for this class / session"
// (kept from 0003 for compatibility); show them as one clear message.
export async function rpc<T>(fn: string, args?: Record<string, unknown>): Promise<Result<T>> {
  const r = await baseRpc<T>(fn, args);
  if (r.error && /^not authorized for this (class|session)$/.test(r.error.message)) {
    return { data: null, error: { code: 'not_your_class', message: r.error.message } };
  }
  return r;
}

export type Forecast = 'likely' | 'uncertain' | 'unlikely' | 'unknown';
export type ContextTag = 'normal' | 'exam_week' | 'after_holiday' | 'after_incident' | 'trip_event';

export type Features = Partial<Record<
  'teacher_session_flow' | 'teacher_predictions' | 'private_notes' | 'you_said_we_did' | 'session_schedules'
  | 'swipe_checkin' | 'class_reflection_loop' | 'safety_valve',
  boolean
>>;

export type TeacherClass = {
  id: string;
  label: string;
  kind: 'homeroom' | 'subject';
  grade: string | null;
  expected_size: number | null;
  forecast: Forecast;
  open_session: { id: string; code: string; expires_at: string; mine: boolean } | null;
  last_session: { id: string; started_at: string } | null;
};

export type Schedule = {
  id: string;
  class_id: string;
  weekday: number;
  start_time: string;
  minutes: number;
  context_tag: ContextTag | null;
  due_now: boolean;
};

export type TeacherHomeData = {
  school: { id: string; name: string; state: string; session_max_minutes: number; teacher_self_service: boolean };
  features: Features;
  context_tags: ContextTag[];
  question_set: { version: number; label: string; topics: string[]; is_draft: boolean };
  classes: TeacherClass[];
  schedules: Schedule[];
};

export type Prediction = { expected_mood: number; expected_topics: string[] } | null;

export type LiveData = {
  session_id: string;
  code: string;
  expires_at: string;
  started_at: string;
  class_id: string;
  class_label: string;
  context_tag: ContextTag | null;
  finished: boolean;
  count: number;
  max_minutes: number;
  can_extend_until: string;
  safety_net: { fi?: string; sv?: string; en?: string } | null;
  prediction: Prediction;
  topics: string[];
};

export type Breakdown =
  | { status: 'insufficient'; total: number; floor: number }
  | { status: 'suppressed_near_unanimous'; total: number }
  | {
      status: 'ok';
      total: number;
      mean: number;
      mood_mode?: 'faces' | 'bands' | 'hidden';
      mood_counts?: Record<string, number> | null;
      mood_bands?: { low: number; mid: number; high: number } | null;
      topic_counts: Record<string, number | null>;
      by_topic?: Record<string, CrossTab>;
    };

export type CrossTab =
  | { status: 'insufficient'; total: number; floor: number }
  | { status: 'suppressed_near_unanimous'; total: number }
  | { status: 'ok'; total: number; mean?: number | null; mood_mode?: 'faces' | 'bands' | 'hidden'; mood_counts?: Record<string, number> | null };

// The teacher's suppressed view of what "Something else" meant (0021).
// Floored and guarded against the "other"-picking subgroup, not the whole
// class — see _concern_breakdown. No mood/mean fields, unlike Breakdown.
export type ConcernBreakdown =
  | { status: 'insufficient'; total: number; floor: number }
  | { status: 'ok'; total: number; concern_counts: Record<string, number | null> };

export type SessionResults = {
  session: {
    id: string;
    class_id: string;
    class_label: string;
    started_at: string;
    ended_at: string;
    context_tag: ContextTag | null;
    question_set_version: number;
    topics: string[];
  };
  results: Breakdown;
  concern_breakdown: ConcernBreakdown;
  features: Features;
  prediction: Prediction;
  note: string | null;
  you_said: {
    id: string;
    body: string;
    created_at: string;
    items?: { body: string; at: string }[];
    visible_until?: string;
    can_add?: boolean;
  } | null;
  can_post_you_said: boolean;
  /** 0026, spec §14.10: visibility only, never a push notification. */
  safety_valve_flagged: boolean;
};

export type HistoryRow = {
  session_id: string;
  started_at: string;
  context_tag: ContextTag | null;
  question_set_version: number;
  status: Breakdown['status'];
  total: number;
  mean: number | null;
  expected_mood: number | null;
  replied: boolean;
  actions?: string[];
};

export type Calibration = {
  sessions: number;
  mean_gap: number | null;
  by_context: Record<string, { n: number; mean_gap: number }>;
};
